import { Link, usePage } from '@inertiajs/react';
import { CheckCircle2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { HackerOnboardingGithubSuccess } from '@/types';
import { DiscordGlyph, GithubGlyph, githubUsername } from './components/marks';
import { OnboardingShell, titleFont } from './components/OnboardingShell';

// github-success/page.tsx + github-success-view.tsx — where the GitHub OAuth callback
// lands a hacker. The four twins differ only in the CTA's label and where it points.
export default function GithubSuccess({
  edition,
  hackerGithub,
}: HackerOnboardingGithubSuccess) {
  const { auth } = usePage().props;
  const username = githubUsername(hackerGithub);
  const githubProfileUrl = `https://github.com/${username}`;
  const githubAvatarUrl =
    auth.user?.image || `https://github.com/${username}.png`;

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-4xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          ¡GitHub{' '}
          <span className="bg-primary px-2 py-1 text-background">
            Conectado!
          </span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Tu cuenta ha sido conectada exitosamente
        </p>
      </div>

      {/* Success Card */}
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <div className="space-y-6">
          {/* Success Alert */}
          <Alert className="border-green-500 bg-green-50">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <AlertTitle className="text-green-800">
              ¡Cuenta conectada exitosamente!
            </AlertTitle>
            <AlertDescription className="text-green-700">
              Tu cuenta de GitHub se ha vinculado correctamente.
            </AlertDescription>
          </Alert>

          {/* Profile Info */}
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <img
                src={githubAvatarUrl}
                alt={`${username} avatar`}
                width={120}
                height={120}
                className="rounded-full border-4 border-green-500"
              />
              <div className="absolute right-0 bottom-0 rounded-full bg-green-500 p-2">
                <GithubGlyph className="h-6 w-6 text-white" />
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 text-center">
              <h2
                className={`font-bold ${titleFont(edition.ph26)} text-3xl text-primary`}
              >
                {auth.user?.name}
              </h2>
              <p className="font-mono text-primary/70 text-sm">
                {auth.user?.email}
              </p>
            </div>
          </div>

          {/* GitHub Info */}
          <div className="border-primary/20 border-t pt-6">
            <div className="bg-green-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`font-bold ${titleFont(edition.ph26)} text-green-800`}
                >
                  Usuario de GitHub:
                </span>
                <a
                  href={githubProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-mono text-green-700 transition-colors hover:underline"
                >
                  @{username}
                </a>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="border-primary/20 border-t pt-6">
            <h3
              className={`mb-4 font-bold ${titleFont(edition.ph26)} text-primary text-xl`}
            >
              Siguiente paso
            </h3>
            <p className="mb-4 font-mono text-primary/80 text-sm">
              Para completar tu onboarding, conecta tu cuenta de Discord para
              unirte al servidor del hackathon.
            </p>
          </div>

          {/* Discord Connect Button */}
          <div className="flex justify-center pt-4">
            <Button size="lg" className="w-full sm:w-auto" asChild>
              <Link href={edition.basePath}>
                <DiscordGlyph className="mr-2 h-5 w-5" />
                {edition.githubSuccessCta}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
}
